import React from "react";

// Semi-circular gauge for scale 1..4. Color = fill color.
export function ScoreGauge({ value, color = "#1E56A0", size = 220 }) {
    const width = size;
    const height = size / 1.6;
    const cx = width / 2;
    const cy = height * 0.9;
    const r = width * 0.42;
    const start = Math.PI; // 180deg
    const end = 0; // 0deg
    const clamped = value == null ? null : Math.max(1, Math.min(4, value));
    // percent from 1..4 → 0..1
    const pct = clamped == null ? 0 : (clamped - 1) / 3;
    const angle = start + (end - start) * pct;
    const arc = (from, to, radius) => {
        const x1 = cx + radius * Math.cos(from);
        const y1 = cy - radius * Math.sin(from);
        const x2 = cx + radius * Math.cos(to);
        const y2 = cy - radius * Math.sin(to);
        const large = Math.abs(to - from) > Math.PI ? 1 : 0;
        const sweep = to < from ? 1 : 0;
        return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} ${sweep} ${x2} ${y2}`;
    };
    const needleX = cx + r * 0.9 * Math.cos(angle);
    const needleY = cy - r * 0.9 * Math.sin(angle);

    return (
        <div className="inline-flex flex-col items-center" data-testid="score-gauge">
            <svg width={width} height={height + 16} viewBox={`0 0 ${width} ${height + 16}`}>
                {/* background arc */}
                <path d={arc(start, end, r)} fill="none" stroke="#E5EEF7" strokeWidth={18} strokeLinecap="round" />
                {/* filled arc */}
                {clamped != null && (
                    <path d={arc(start, angle, r)} fill="none" stroke={color} strokeWidth={18} strokeLinecap="round" />
                )}
                {/* ticks */}
                {[1, 2, 3, 4].map((n) => {
                    const t = start + (end - start) * ((n - 1) / 3);
                    const tx = cx + (r - 14) * Math.cos(t);
                    const ty = cy - (r - 14) * Math.sin(t);
                    return (
                        <text
                            key={n}
                            x={tx}
                            y={ty}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="11"
                            fontFamily="Inter, sans-serif"
                            fill="#0E1B2C99"
                        >
                            {n}
                        </text>
                    );
                })}
                {/* needle */}
                {clamped != null && (
                    <>
                        <line
                            x1={cx}
                            y1={cy}
                            x2={needleX}
                            y2={needleY}
                            stroke="#0B2545"
                            strokeWidth="3"
                            strokeLinecap="round"
                        />
                        <circle cx={cx} cy={cy} r={6} fill="#0B2545" />
                    </>
                )}
                {/* label */}
                <text
                    x={cx}
                    y={cy - r * 0.55}
                    textAnchor="middle"
                    fontFamily="Fraunces, serif"
                    fontSize={width * 0.15}
                    fontWeight="700"
                    fill="#0B2545"
                >
                    {clamped != null ? clamped.toFixed(2) : "—"}
                </text>
                <text
                    x={cx}
                    y={cy - r * 0.55 + 18}
                    textAnchor="middle"
                    fontFamily="Inter, sans-serif"
                    fontSize="11"
                    fill="#0E1B2C99"
                    letterSpacing="1"
                >
                    {clamped != null ? "OF 4.00" : "NOT YET SCORED"}
                </text>
            </svg>
        </div>
    );
}

// Color band by score
export function scoreBandColor(s) {
    if (s == null) return "#CBD5E1"; // muted
    if (s <= 1.5) return "#DC2626";
    if (s <= 2.5) return "#D97706";
    if (s <= 3.5) return "#2563EB";
    return "#059669";
}
